/**
 * StagingWorkingState — the module's EPHEMERAL working-state context (E1,
 * MODULE-CONTRACT §10.1). Holds the active-room cursor + popup/wizard flags in a
 * module-scoped `constate` context mounted INSIDE `Staging.component`, NOT in the
 * Zustand persisted store. Because it lives outside the persisted store it is
 * invisible to `hydrateFromDraft` / `resetPersistedState` → it cannot cause an
 * F-B1-class clobber by construction. The working state is a cache/cursor; the
 * DTO (via `useStagingConfig`) is the truth.
 *
 * It replaces the legacy `StyleSelection` + `useActiveRoom` + `RoomAPI` pairing:
 *   - `activeRoomIndex` (legacy `useActiveRoom.ts:9`) + popup flags (legacy
 *     `StyleSelection.context.ts:38-39`) — ephemeral `useState`.
 *   - `allRooms` / `activeRoom` — DERIVED from the persisted DTO rooms (sorted by
 *     `roomCreationSortingOrder`, `useActiveRoom.ts:11`) + the file-hub thumbnail
 *     join, collapsing the legacy 1:N `RoomAPI` pairing to the v2 1:1 identity
 *     (roomKey === visual id).
 *   - the bounds-clamp (`useActiveRoom.ts:14-18`) keeps the cursor valid when a
 *     room is removed.
 *
 * It also re-exposes the ops-hook writers (`patchRoom`/`removeRoom`) + derived
 * selection flags so the lifted leaves have a single `useStagingWorkingState()`
 * surface (the native analog of the legacy `useStyleSelection`).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import constate from 'constate'

import { StagingType } from 'models/virtualStaging'
import { VirtualFurnitureRoomType } from 'constants/virtualFurniture'
import type { CartItemId } from 'models/purchaseFlowRevamp'

import { usePfV2VisualUpload } from '../../fileHub'
import { useStagingConfig } from './useStagingConfig'
import { normalizeRoom, type StagingDraftRoomDTO } from './staging.dto'
import { mapGet } from './stagingRoom.logic'

/** Which room-item popup is open (native port of the legacy enum). */
export enum RoomItemPopupType {
  FURNITURE = 'FURNITURE',
  DECORATION = 'DECORATION',
  WALL = 'WALL',
  FLOOR = 'FLOOR',
}

/** A persisted room joined with its file-hub thumbnail — the shape the lifted UI
 *  consumes (native, plain-JS analog of the legacy `ComputedStagingRoom`). */
export interface StagingViewRoom extends StagingDraftRoomDTO {
  images: Array<{ id: string; url?: string }>
}

interface StagingWorkingStateProps {
  cartItemId: CartItemId
}

function useStagingWorkingStateSetup({ cartItemId }: StagingWorkingStateProps) {
  const { rooms, patchRoom, removeRoom, isComplete } = useStagingConfig(cartItemId)
  const { visuals } = usePfV2VisualUpload()

  // ── Ephemeral cursor + popup flags (NOT persisted) ─────────────────────────
  const [activeRoomIndex, setActiveRoomIndex] = useState<number>(0)
  const [isSpecialRequestPopupOpen, setIsSpecialRequestPopupOpen] = useState<boolean>(false)
  const [roomItemPopupType, setRoomItemPopupType] = useState<RoomItemPopupType | null>(null)

  // ── Thumbnail join (file-hub visual URL by roomKey === visual id) ──────────
  const urlByVisualId = useMemo(() => {
    const map: Record<string, string | undefined> = {}
    for (const visual of visuals) map[visual.id] = visual.webUrl ?? undefined
    return map
  }, [visuals])

  // ── Derived, sorted room list (bucket b — re-derivable) ────────────────────
  // Field-merge each persisted room (partial-DTO invariant) so the leaves can read
  // `activeRoom.roomTypes.length` etc. without guarding every array access.
  const allRooms = useMemo<StagingViewRoom[]>(
    () =>
      Object.entries(rooms)
        .map(([roomKey, raw]) => normalizeRoom(roomKey, raw))
        .sort((a, b) => a.roomCreationSortingOrder - b.roomCreationSortingOrder)
        .map((room) => ({ ...room, images: [{ id: room.key, url: urlByVisualId[room.key] }] })),
    [rooms, urlByVisualId]
  )

  // Bounds-clamp the cursor when rooms shrink (legacy `useActiveRoom.ts:14-18`).
  useEffect(() => {
    if (activeRoomIndex >= allRooms.length && allRooms.length > 0) {
      setActiveRoomIndex(allRooms.length - 1)
    }
  }, [activeRoomIndex, allRooms.length])

  const activeRoom = allRooms[activeRoomIndex] as StagingViewRoom | undefined

  const openRoomItemPopup = useCallback((popupType: RoomItemPopupType) => {
    setRoomItemPopupType(popupType)
  }, [])

  // ── Derived selection flags (native port of legacy `useStyleSelection`) ────
  const selectedRemovableObject =
    mapGet(activeRoom?.renovations ?? [], VirtualFurnitureRoomType.WHOLE_ROOM) ?? activeRoom?.furnitureRemoval ?? undefined
  const isSomeFurnitureSelected = (activeRoom?.styleCodeMap.length ?? 0) > 0
  const isAllFurnitureSelected = !!activeRoom?.hasSelectedAllStyleCodes
  const isDecorationSelected = (activeRoom?.decorations.length ?? 0) > 0
  const isWallSelected = !!mapGet(activeRoom?.renovations ?? [], VirtualFurnitureRoomType.WALL_MATERIALS)
  const isFloorSelected = !!mapGet(activeRoom?.renovations ?? [], VirtualFurnitureRoomType.FLOORING)
  const isRoomItemSelected = isSomeFurnitureSelected || isDecorationSelected || isWallSelected || isFloorSelected
  const isBKBNDecide = activeRoom?.type === StagingType.BKBN
  const isRoomEmptyType =
    activeRoom?.roomTypes.length === 1 && activeRoom.roomTypes.includes(VirtualFurnitureRoomType.EMPTY_ROOM)

  return {
    // durable writers (DTO is the truth)
    patchRoom,
    removeRoom,
    isComplete,
    // derived room list + cursor
    allRooms,
    activeRoom,
    activeRoomIndex,
    setActiveRoomIndex,
    // popup flags
    isSpecialRequestPopupOpen,
    setIsSpecialRequestPopupOpen,
    roomItemPopupType,
    setRoomItemPopupType,
    openRoomItemPopup,
    // derived selection flags
    selectedRemovableObject,
    isSomeFurnitureSelected,
    isAllFurnitureSelected,
    isDecorationSelected,
    isWallSelected,
    isFloorSelected,
    isRoomItemSelected,
    isBKBNDecide,
    isRoomEmptyType,
  }
}

export const [StagingWorkingStateProvider, useStagingWorkingState] = constate(useStagingWorkingStateSetup)
